import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { DataTableColumn, DataType } from '@globaleris/ui.library'
import {
  makeStyles,
  CardContent,
  Button,
  Box,
  Typography,
  useTheme,
  Link,
  Tooltip,
  IconButton
} from '@material-ui/core'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { FlexibilitiesModel } from '../../../../api/Argentia/endpoints/flexibilities/model/flexibilities'

import ButtonWithPermissions from '../../../../components/buttonWithPermissions/buttonWithPermissions'
import { CardWithTransition } from '../../../../components/shared/cardWithTransition/CardWithTransition'
import { FlexibilitiesTable } from '../../../../components/shared/flexibilitiesTable/flexibilitiesTable'
import useFlexibilities from '../../../../components/shared/flexibilitiesTable/flexibilitiesTable.hooks'
import { LoadingCard } from '../../../../components/shared/loadingCard/loadingCard'
import { NonAuthRoutes } from '../../../../model'
import { storeActiveTabForClientFilePage, storeActiveFlexibility } from '../../../../redux'
import { EmptyContent } from '../../../clients/file/components/shared/emptyCard'
import { FlexibilityTableModel } from '../../../flexibility/flexibility.Model'
import { styles } from '../contracts.flexibilities.styles'
import { FlexibilitiesCardProps } from '../contracts.flexibilities.types'
import { faBezierCurve } from '@fortawesome/free-solid-svg-icons'
import { Header } from '../../../clients/file/components/shared/header'
import { fetchSettlementGroupsByContract } from '../../contracts.service'
import { SettlementGroup } from '../../../../api/Argentia/endpoints/settlementGroups/model/settlementGroups'
import { ContractModel } from '../../../../api/Argentia/model/contracts'
import { Info } from '@material-ui/icons'
import { ViewGroupsDialog } from './viewGroupsDialog'
import { consolidateSettlementGroupsMapper } from '../../../settlementGroups/settlementGroups.mapper'
import CommentIcon from '@material-ui/icons/Comment'

export const FlexibilitiesCard: React.FC<FlexibilitiesCardProps> = ({
  contract,
  commodity,
  error,
  // onClickAdd,
  // onClickEdit,
  onRetry
}) => {
  const [t] = useTranslation(['globalerisLibrary', 'clientFile', 'flexibility', 'enums'])
  const history = useHistory()

  const currentTheme = useTheme()
  const useStyles = makeStyles(styles(currentTheme))
  const classes = useStyles()

  const { flexibilities, isFetchingFlexibilities, refreshFlexibilities } = useFlexibilities({
    commodity,
    contractCode: contract?.contractCode
  })

  const [relatedGroups, setRelatedGroups] = React.useState<SettlementGroup[]>([])
  const [showGroupsDialog, setShowGroupsDialog] = React.useState<boolean>(false)

  const hasRelatedGroups = flexibilities?.some(f => f.isSettlementGroup)

  const FlexibilitiesHeader = () =>
    React.useMemo(() => {
      return (
        <Header
          avatarIcon={<FontAwesomeIcon size="lg" icon={faBezierCurve as IconProp} />}
          title={t('clientFile:components.flexibilitiesCard.title')}
          titleVariant="secondary"
        />
      )
    }, [t])

  const handleViewAdditionals = (
    flexibility: FlexibilitiesModel,
    activeTable: 'PRICE_COMPONENTS' | 'FLEXIBILITY' = 'FLEXIBILITY'
  ) => {
    storeActiveTabForClientFilePage(1, activeTable)
    storeActiveFlexibility(flexibility)
  }

  const handleClickViewPriceComponents = (flexibility: FlexibilityTableModel) => {
    const flex = flexibilities?.find(item => item.id === flexibility.id)

    if (flex) {
      storeActiveFlexibility(flex)
      history.push(NonAuthRoutes.ContractPriceComponents)
    }
  }

  const handleAddFlexibility = async () => {
    history.push(NonAuthRoutes.flexibilityAdd, {
      contractID: contract?.id,
      commodity: commodity
    })
    return true
  }

  // DataTable Config Starts
  const columns: DataTableColumn[] = [
    {
      name: 'id',
      display: false,
      type: DataType.NUMBER
    },
    {
      name: 'subtype',
      label: t('flexibility:dataTable.columns.subtype'),
      type: DataType.TEXT
      //columnWidth: 250
    },
    {
      name: 'description',
      label: t('flexibility:dataTable.columns.description'),
      type: DataType.TEXT
      //columnWidth: 250
    },
    {
      name: 'period',
      label: t('flexibility:dataTable.columns.period'),
      type: DataType.TEXT
      //columnWidth: 250
    },
    {
      name: 'contract',
      label: t('flexibility:dataTable.columns.contract'),
      type: DataType.TEXT
      //columnWidth: 250
    },
    {
      name: 'cups',
      label: t('flexibility:dataTable.columns.cups'),
      type: DataType.TEXT
      //columnWidth: 250
    },
    {
      name: 'calculationType',
      label: t('flexibility:dataTable.columns.calculationType'),
      type: DataType.TEXT
      //columnWidth: 250
    }
  ]

  const checkGroups = async (contract?: ContractModel) => {
    if (contract) {
      await fetchSettlementGroupsByContract(contract).then(res => {
        if (res.success && res.data && res.data.length > 0) {
          setRelatedGroups(consolidateSettlementGroupsMapper(res.data))
        }
      })
    }
  }

  useEffect(() => {
    checkGroups(contract)
  }, [contract])

  if (isFetchingFlexibilities) {
    return <LoadingCard />
  }

  if (error) {
    return (
      <CardWithTransition show={true} raised>
        <FlexibilitiesHeader />
        <EmptyContent title={t('clientFile:components.flexibilitiesCard.errorCard.errorMessage')}>
          <ButtonWithPermissions
            hasPermission={true}
            size="small"
            color="primary"
            variant="contained"
            onClick={onRetry}
          >
            {t('clientFile:components.flexibilitiesCard.errorCard.buttonLabel')}
          </ButtonWithPermissions>
        </EmptyContent>
      </CardWithTransition>
    )
  }

  return (
    <>
      <CardWithTransition show={true} raised>
        <FlexibilitiesHeader />
        {!error && flexibilities && flexibilities.length > 0 ? (
          <>
            <CardContent className={classes.cardContent}>
              <FlexibilitiesTable
                flexibilities={flexibilities}
                isFetchingFlexibilities={isFetchingFlexibilities}
                refreshFlexibilities={refreshFlexibilities}
                columnsConfig={columns}
                onClickViewPriceComponents={handleClickViewPriceComponents}
                onClickAddFlexibility={handleAddFlexibility}
              />
            </CardContent>
          </>
        ) : (
          <EmptyContent title={t('clientFile:components.flexibilitiesCard.emptyCard.title')}>
            <Button size="small" variant="contained" color="primary" onClick={handleAddFlexibility}>
              {t('clientFile:components.flexibilitiesCard.actions.create')}
            </Button>
          </EmptyContent>
        )}
      </CardWithTransition>
      {contract && relatedGroups && relatedGroups.length > 0 ? (
        <>
          <Box
            marginTop={2}
            marginLeft={2}
            display={'flex'}
            flexDirection={'row'}
            justifyContent={'flex-start'}
          >
            <Box marginX={0.5}>
              <Typography variant="subtitle2" style={{ fontStyle: 'italic' }}>
                {t('clientFile:components.flexibilitiesCard.groupsRelated.title')}
              </Typography>
            </Box>
            <Box>
              <Tooltip
                title={t('clientFile:components.flexibilitiesCard.groupsRelated.viewDetails')!}
                placement="bottom"
              >
                <IconButton
                  color="primary"
                  size="small"
                  edge="end"
                  style={{ marginTop: 0, padding: 0 }}
                  onClick={() => {
                    setShowGroupsDialog(true)
                  }}
                >
                  <Info fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          <ViewGroupsDialog
            show={showGroupsDialog}
            groups={relatedGroups}
            contract={contract}
            onCloseDialog={() => setShowGroupsDialog(false)}
          />
        </>
      ) : null}
    </>
  )
}
